import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Chip } from '@mui/material';
import { Icon } from '@iconify/react';
import UnifiedTransactionModal from '../shared/UnifiedTransactionModal';
import UnifiedTransactionTable from '../shared/UnifiedTransactionTable';
interface Transaction {
  id: string;
  amount: number;
  currency: string;
  payoutAmount: number;
  payoutCurrency: string;
  mto: string;
  type: string;
  branchCode: string;
  agentCode: string;
  createdAt: string;
  paymentMethod: string;
  status: string;
  senderName: string;
  receiverName: string;
  parties: any;
  accounts: any;
  overview: any;
  idType: string;
  idNumber: string;
  contactNumber: string;
  receiverCountry: string;
  province: string;
  city: string;
  idExpiry: string;
  email: string;
  dateOfBirth: string;
  zipCode: string;
  address: string;
}
const TransactionApprovalPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const loadTransactions = () => {
    const stored = localStorage.getItem('initiatedTransactions');
    if (stored) {
      const allTransactions = JSON.parse(stored);
      const pendingTransactions = allTransactions.filter((t: Transaction) => t.status === 'Pending Checker Approval');
      setTransactions(pendingTransactions);
    }
  };
  useEffect(() => {
    loadTransactions();
    const handleTransactionAdded = () => {
      loadTransactions();
    };
    window.addEventListener('transactionAdded', handleTransactionAdded);
    window.addEventListener('transactionUpdated', handleTransactionAdded);
    return () => {
      window.removeEventListener('transactionAdded', handleTransactionAdded);
      window.removeEventListener('transactionUpdated', handleTransactionAdded);
    };
  }, []);
  const handleViewDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsModal(true);
  };
  const handleApprove = (decision: string, reason: string) => {
    if (!selectedTransaction) return;
    const stored = localStorage.getItem('initiatedTransactions');
    if (!stored) return;
    const allTransactions = JSON.parse(stored);
    const updatedTransactions = allTransactions.map((t: Transaction) => {
      if (t.id === selectedTransaction.id) {
        if (decision === 'completed') {
          return {
            ...t,
            status: 'Processing',
            processingStartTime: new Date().toISOString(),
            checkerDecision: decision,
            checkerReason: reason,
            checkerTimestamp: new Date().toISOString(),
            overview: {
              ...t.overview,
              status: 'Processing'
            },
            timeline: [...t.timeline, {
              title: 'Checker Approval',
              description: 'Transaction approved by checker',
              timestamp: new Date().toLocaleString(),
              status: 'completed'
            }, {
              title: 'Balance Check',
              description: 'Verifying balance...',
              timestamp: new Date().toLocaleString(),
              status: 'current'
            }]
          };
        } else {
          return {
            ...t,
            status: 'Rejected',
            checkerDecision: decision,
            checkerReason: reason,
            checkerTimestamp: new Date().toISOString(),
            rejectionReason: reason,
            rejectedAt: new Date().toISOString(),
            rejectedBy: 'Checker'
          };
        }
      }
      return t;
    });
    localStorage.setItem('initiatedTransactions', JSON.stringify(updatedTransactions));
    window.dispatchEvent(new Event('transactionUpdated'));
    setShowDetailsModal(false);
    setSelectedTransaction(null);
    loadTransactions();
  };
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };
  const columns = [{
    id: 'id',
    label: 'Transaction ID',
    filterable: true,
    filterType: 'text' as const
  }, {
    id: 'amount',
    label: 'Amount FCY',
    filterable: true,
    filterType: 'number' as const
  }, {
    id: 'payoutAmount',
    label: 'Amount PKR'
  }, {
    id: 'mto',
    label: 'MTO',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['UAE Exchange', 'Western Union', 'MoneyGram', 'Ria Money Transfer', 'Al Ansari Exchange']
  }, {
    id: 'type',
    label: 'Type'
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    filterable: true,
    filterType: 'text' as const
  }, {
    id: 'agentCode',
    label: 'Agent Code'
  }, {
    id: 'createdAt',
    label: 'Created',
    format: (value: string) => value ? new Date(value).toLocaleString() : '—'
  }, {
    id: 'paymentMethod',
    label: 'Payment Method',
    format: (value: string) => value || 'Bank Transfer'
  }, {
    id: 'status',
    label: 'Status',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['Pending Checker Approval'],
    format: (value: string) => <Chip label={value} color="warning" size="small" data-id="element-49" />
  }];
  return <Box sx={{
    p: 3,
    bgcolor: 'background.default',
    minHeight: '100vh'
  }} data-id="element-50">
      <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      mb: 3
    }} data-id="element-51">
        <Icon icon="lucide:check-circle" width={28} height={28} color="#0052CC" data-id="element-52" />
        <Typography variant="h4" sx={{
        fontWeight: 700,
        color: '#0052CC'
      }} data-id="element-53">
          Transaction Checker Inbox
        </Typography>
      </Box>

      {transactions.length === 0 ? <Alert severity="info" sx={{
      mb: 3
    }} data-id="element-54">
          <Typography variant="body2" fontWeight={600} data-id="element-55">
            No transactions pending approval
          </Typography>
        </Alert> : <Alert severity="warning" sx={{
      mb: 3
    }} data-id="element-56">
          <Typography variant="body2" fontWeight={600} data-id="element-57">
            You have {transactions.length} transaction
            {transactions.length !== 1 ? 's' : ''} pending approval
          </Typography>
        </Alert>}

      <UnifiedTransactionTable columns={columns} data={transactions} onViewDetails={handleViewDetails} title={`Pending Transactions (${transactions.length})`} emptyMessage="No transactions pending approval" data-id="element-58" />

      {selectedTransaction && <UnifiedTransactionModal transaction={selectedTransaction} isOpen={showDetailsModal} onClose={() => {
      setShowDetailsModal(false);
      setSelectedTransaction(null);
    }} mode="approve" onApprove={handleApprove} data-id="element-59" />}
    </Box>;
};
export default TransactionApprovalPage;